import { ChangeDetectionStrategy, Component, EventEmitter, Output, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { filter, firstValueFrom, from, map, switchMap, take } from 'rxjs';
import { NewFeatureDotComponent } from '../account-gallery-shared/components/new-feature-dot/new-feature-dot.component';
import { ExportGalleryDialogComponent } from '../gallery-shared/dialogs/export-gallery-dialog/export-gallery-dialog.component';
import { DeleteGalleryDialogComponent } from '../gallery/dialogs/delete-gallery-dialog/delete-gallery-dialog.component';
import { DuplicateGalleryDialogComponent } from '../gallery/dialogs/duplicate-gallery-dialog/duplicate-gallery-dialog.component';
import { GalleryInfoDialogComponent } from '../gallery/dialogs/gallery-info-dialog/gallery-info-dialog.component';
import { MoveGalleryDialogComponent } from '../gallery/dialogs/move-gallery-dialog/move-gallery-dialog.component';
import { ShareGalleryDialogComponent } from '../gallery/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { GallerySettingsMenuComponent } from '../gallery/menus/gallery-settings-menu/gallery-settings-menu.component';
import { GalleryThemeMenuComponent } from '../gallery/menus/gallery-theme-menu/gallery-theme-menu.component';
import { GROWTHBOOK } from '../growthbook';
import { calcGalleryPermissions } from '../lib/access-level-helpers';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { AssetMoveService } from '../services/asset-move.service';
import { AssetService } from '../services/asset.service';
import { DialogService } from '../services/dialog.service';
import { GalleryAccessLevelService } from '../services/gallery-access-level.service';
import { GalleryService } from '../services/gallery.service';
import { SectionService } from '../services/section.service';
import { ShareService } from '../services/share.service';
import { StateService } from '../services/state.service';
import { TenantStorageService } from '../services/tenant-storage.service';
import { ToastService } from '../services/toast.service';
import { ButtonMenuItemComponent } from '../shared/components/button-menu-item/button-menu-item.component';
import { LoadingIndicatorComponent } from '../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../shared/components/svg/svg.component';
import { ButtonDirective } from '../shared/directives/button.directive';
import { ResizeDirective } from '../shared/directives/resize.directive';
import { TranslatePipe } from '../shared/pipes/translate.pipe';

@Component({
  selector: 'app-gallery-context-menu',
  templateUrl: './gallery-context-menu.component.html',
  styleUrls: ['./gallery-context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonDirective,
    SvgComponent,
    NewFeatureDotComponent,
    ButtonMenuItemComponent,
    ResizeDirective,
    TranslatePipe,
    LoadingIndicatorComponent,
  ],
})
export class GalleryContextMenuComponent {
  public state = inject(StateService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private dialog = inject(DialogService);
  private galleryService = inject(GalleryService);
  private toasts = inject(ToastService);
  private shareService = inject(ShareService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private sectionService = inject(SectionService);
  private assetMoveService = inject(AssetMoveService);
  private assetService = inject(AssetService);
  private tenantStorageService = inject(TenantStorageService);
  public growthBook = inject(GROWTHBOOK);
  public router = inject(Router);
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly _gallery = input<GalleryDto>(undefined, { alias: 'gallery' });
  gallery = toSignal(
    toObservable(this._gallery).pipe(switchMap(gallery => this.galleryService.getGallery(gallery.id)))
  );
  readonly galleryHeaderMode = input<boolean>(undefined);
  readonly navigateOnDuplicate = input<boolean>(undefined);
  readonly advancedMode = input<boolean>(false);
  @Output() onClose = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onMoved = new EventEmitter();
  private accessLevel = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.accessLevelService.depricatedGetAccessLevel(gallery))
    )
  );
  galleryPermissions = computed(() => calcGalleryPermissions(this.gallery(), this.accessLevel()));
  tenant = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      map(gallery => gallery.tenant as TenantDto)
    )
  );
  isTeamMember = computed(() => {
    const tenant = this.tenant();
    if (!tenant) return false;
    return this.state.tenants?.some(t => t.id === tenant.id);
  });
  activeTenant = toSignal(this.state.activeTenant$);
  previewLink = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.shareService.getShareLink(gallery)),
      map(link => {
        return link.url + ((link.url.indexOf('?') > -1 ? '&' : '?') + 'preview=true');
      })
    )
  );

  openSettingsDialog() {
    this.dialog.open(GallerySettingsMenuComponent, { gallery: this.gallery() });
  }

  openThemeMenu() {
    this.dialog.open(GalleryThemeMenuComponent, { gallery: this.gallery() });
  }

  openShareDialog() {
    this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery() });
  }

  duplicate() {
    this.tenantStorageService
      .getStorage()
      .pipe(take(1))
      .subscribe(storage => {
        const total = storage.total;
        const used = storage.used;
        const remaining = total - used;
        // const gallerySize =
        //   stats.total_assets_original_size_including_subgalleries + stats.total_assets_version_size_including_subgalleries;
        const gallerySize = 0; // temp until backend can fix overview performance issues.
        if (gallerySize > remaining) {
          this.toasts.add('Not enough Storage remaining', 'error');
          return false;
        }

        from(this.dialog.open(DuplicateGalleryDialogComponent, { gallery: this.gallery() })).subscribe(
          (gallery: GalleryDto) => {
            if (gallery) {
              if (this.navigateOnDuplicate()) {
                this.router.navigate(['/a', 'gallery', gallery.id]);
              }
            }
          }
        );
      });
  }

  async openMoveToDialog() {
    const resp = <{ gallery: GalleryDto; action: string }>(
      await this.dialog.open(MoveGalleryDialogComponent, { gallery: this.gallery() })
    );
    if (!resp) return;
    this.onMoved.emit();
  }

  async openExportDialog() {
    this.dialog.open(ExportGalleryDialogComponent, { gallery: this.gallery() });
  }

  async openGalleryInfoDialog() {
    this.dialog.open(GalleryInfoDialogComponent, { gallery: this.gallery(), inGallery: this.galleryHeaderMode() });
  }

  async openDeleteGalleryDialog() {
    const resp = await this.dialog.open(DeleteGalleryDialogComponent, { gallery: this.gallery() });
    if (resp) {
      const gallery = this.gallery();
      this.analytics.track('gallery_deleted', true, {
        gallery_id: gallery.id,
        type: gallery.section ? 'sub-gallery' : 'main-gallery',
      });
      this.toasts.add('Gallery deleted', 'success');
      this.onDelete.emit(gallery);
      this.onClose.emit();
    }
  }

  canDelete() {
    if (!this.galleryPermissions().can_edit) return false;
    if (this.isTeamMember()) return true;
    return !!this.gallery().section;
  }

  toggleSubGalleryHide() {
    const public_access = this.gallery().public_access === 'no_access' ? null : 'no_access';
    this.galleryService.updateGallery(this.gallery().id, { public_access });
  }

  async convertToAssetSection() {
    const parentGallery = await firstValueFrom(this.galleryService.getGallery(this.gallery().parent_gallery_id));
    const sections = await firstValueFrom(this.sectionService.getSections(parentGallery.id));
    const maxPosition = sections.reduce((max, section) => Math.max(max, section.position), 0);
    const section = await firstValueFrom(this.sectionService.createSection(parentGallery.id, 'asset', maxPosition + 1));
    const assets = await this.assetService.getAssetsForGallery(this.gallery().id);
    const resp = await this.assetMoveService.moveAssets(assets, parentGallery, section);
    await firstValueFrom(this.sectionService.updateSection(section, { position: section.position }));
    if (resp !== true) return;
    await this.galleryService.deleteGallery(this.gallery());
  }
}
